import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { useLink } from '@grandvisionhq/frk-routing';
import { getProductKeyFromCTType } from '@grandvisionhq/utils';
import '../types/product.js';
import { isSubscriptionPurchase } from '../types/typeguards.js';
import { getDisplayPrice } from '../utils/get-display-price.js';
import { updatePurchaseType } from './actions.js';
import { productDetailContextReducer, initialState } from './reducer.js';

/**
 * Product Context
 */ const ProductContext = /*#__PURE__*/ React.createContext({});
ProductContext.displayName = 'ProductContext';
/**
 * Product Provider
 */ function ProductProvider({ children , sku , selfUrl , product , routes , facetConfigList , specificationConfiguration , bookAppointmentRouteConfig , deliveryIntervalOptionsFallback  }) {
    const { variants  } = product;
    const [state, dispatch] = React.useReducer(productDetailContextReducer, swcHelpers.objectSpread({}, initialState, {
        activeSku: sku
    }));
    /**
   * Typescript v4 is unable to handle union types with the .find() method.
   * More information here: https://github.com/microsoft/TypeScript/issues/44373
   *
   * The issue is fixed in v5, so we can remove the @ts-expect-error and the
   * cast for `const activeVariant: Variant` when we upgrade.
   */ const activeVariant = React.useMemo(// @ts-expect-error ts(2349)
    ()=>variants.find((variant)=>variant.sku === state.activeSku
        ) || variants[0]
    , [
        state,
        variants
    ]);
    const { selectedPurchaseType  } = state;
    if (!activeVariant.isAvailableForSubscription && isSubscriptionPurchase(selectedPurchaseType)) {
        dispatch(updatePurchaseType({
            purchaseType: 'single-purchase'
        }));
    }
    // Update the display price based on the selected purchase type (single purchase or subscription)
    const displayPrice = React.useMemo(()=>getDisplayPrice(activeVariant, state)
    , [
        activeVariant,
        selectedPurchaseType
    ]);
    const productLink = useLink({
        route: 'products.detail',
        params: {
            slug: activeVariant.slug,
            sku: activeVariant.sku,
            key: getProductKeyFromCTType(product.productType)
        }
    });
    const productUrl = `${selfUrl}${productLink === null || productLink === void 0 ? void 0 : productLink.href}`;
    const value = {
        activeVariant,
        displayPrice,
        dispatch,
        productUrl,
        product,
        routes,
        facetConfigList,
        selectedPurchaseType,
        specificationConfiguration,
        bookAppointmentRouteConfig,
        deliveryIntervalOptionsFallback
    };
    return /*#__PURE__*/ React.createElement(ProductContext.Provider, {
        value: value
    }, children);
}

export { ProductContext, ProductProvider };
//# sourceMappingURL=ProductContext.js.map
